import { BreakpointState } from "@angular/cdk/layout";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    Signal,
    ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Subscription } from "rxjs";
import { distinctUntilChanged, filter } from "rxjs/operators";

import { LayoutFacade } from "@hermes/aphrodite/layout";
import { Context, WINDOW } from "@hermes/app-core";
import {
    DEFAULT_FILTER_BAR_HEIGHT,
    DisplayMode,
    FILTER_DESKTOP_EMPTY,
    FILTER_DESKTOP_FIXED_TOP,
    FILTER_DESKTOP_FIXED_UNDER_HEADER,
    GridFacade,
    GridSortByOption,
    SelectedFilter,
} from "@hermes/states/grid-filters-state";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

@Component({
    selector: "h-grid-filters-bar",
    templateUrl: "./grid-filters-bar.component.html",
    styleUrls: ["./grid-filters-bar.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridFiltersBarComponent implements OnInit, OnDestroy {
    @ViewChild("filterBar")
    public filterBar: ElementRef;
    @ViewChild("emptySpace")
    public emptySpace: ElementRef;
    public displayMode: DisplayMode = "grid";
    public filtersBarDisplayMode: "fixed" | "relative" | "fixed-under-header" =
        "relative";
    public selectedSort: string;
    public selectedFiltersSignal: Signal<SelectedFilter[]> = toSignal(
        this.gridFacade.selectedFilters$,
        { initialValue: [] },
    );
    public headerHeight: number;
    public mainHeaderHeight: number;
    public startPosition: number;
    public isMobileFiltersDialogOpen = false;

    public isServer = true;
    public isMobile = true;

    private subscription: Subscription = new Subscription();

    constructor(
        public detector: ChangeDetectorRef,
        @Inject(WINDOW) private window: Window,
        private gridFacade: GridFacade,
        private layoutFacade: LayoutFacade,
        private ngZone: NgZone,
        private renderer: Renderer2,
        private context: Context,
        private breakpointService: BreakpointService,
    ) {}

    /**
     * arrow function to treat it outside angular
     */
    public onScroll = (): void => {
        const windowScrollY = this.window.scrollY;
        const fromFilterBarToTopHeight =
            this.headerHeight + this.filterBar.nativeElement.offsetTop;
        if (windowScrollY > fromFilterBarToTopHeight) {
            this.renderer.addClass(
                this.filterBar.nativeElement,
                FILTER_DESKTOP_FIXED_TOP,
            );
            this.renderer.addClass(
                this.emptySpace.nativeElement,
                FILTER_DESKTOP_EMPTY,
            );
            this.renderer.setStyle(this.filterBar.nativeElement, "top", "0px");
            this.filtersBarDisplayMode = "fixed";
        } else {
            this.renderer.removeClass(
                this.filterBar.nativeElement,
                FILTER_DESKTOP_FIXED_TOP,
            );
            this.renderer.removeClass(
                this.emptySpace.nativeElement,
                FILTER_DESKTOP_EMPTY,
            );
            this.renderer.removeStyle(this.filterBar.nativeElement, "top");
            this.filtersBarDisplayMode = "relative";
        }
        if (
            this.startPosition >= windowScrollY &&
            windowScrollY > fromFilterBarToTopHeight
        ) {
            this.filtersBarDisplayMode = "fixed-under-header";
            this.renderer.addClass(
                this.filterBar.nativeElement,
                FILTER_DESKTOP_FIXED_UNDER_HEADER,
            );
            this.renderer.addClass(
                this.emptySpace.nativeElement,
                FILTER_DESKTOP_EMPTY,
            );
            this.renderer.setStyle(
                this.filterBar.nativeElement,
                "top",
                `${this.mainHeaderHeight}px`,
            );
        } else {
            this.renderer.removeClass(
                this.filterBar.nativeElement,
                FILTER_DESKTOP_FIXED_UNDER_HEADER,
            );
            this.renderer.removeStyle(this.filterBar.nativeElement, "top");
        }
        this.startPosition = windowScrollY;
    };

    public ngOnInit(): void {
        this.isServer = this.context.isInServerMode();
        if (this.context.isInBrowserMode()) {
            this.subscription.add(
                this.breakpointService
                    .mediumBreakpointObserver()
                    .subscribe((value: BreakpointState) => {
                        this.isMobile = !value.matches;
                        this.detector.markForCheck();
                    }),
            );
        }

        this.subscription.add(
            this.gridFacade.sortBy$.subscribe((sort: string) => {
                this.selectedSort = sort;
                this.detector.markForCheck();
            }),
        );
        this.subscription.add(
            this.gridFacade.displayMode$.subscribe(
                (displayMode: DisplayMode) => {
                    this.displayMode = displayMode;
                    this.detector.markForCheck();
                },
            ),
        );

        this.subscription.add(
            this.layoutFacade.headerMainHeight$
                .pipe(filter((height) => !!height))
                .subscribe((height: number) => {
                    this.headerHeight = height;
                    this.detector.markForCheck();
                }),
        );

        this.subscription.add(
            this.layoutFacade.headerMainHeight$
                .pipe(distinctUntilChanged())
                .subscribe((headerInnerHeight: number) => {
                    this.mainHeaderHeight = headerInnerHeight;
                    if (headerInnerHeight && this.filterBar) {
                        // Case zoom while fixed-under-header class added.
                        if (
                            this.filtersBarDisplayMode === "fixed-under-header"
                        ) {
                            this.renderer.setStyle(
                                this.filterBar.nativeElement,
                                "top",
                                `${headerInnerHeight}px`,
                            );
                        }

                        if (this.mainHeaderHeight > DEFAULT_FILTER_BAR_HEIGHT) {
                            this.renderer.setStyle(
                                this.filterBar.nativeElement,
                                "height",
                                `${headerInnerHeight}px`,
                            );
                        } else {
                            this.renderer.removeStyle(
                                this.filterBar.nativeElement,
                                "height",
                            );
                        }
                    }
                    this.detector.markForCheck();
                }),
        );

        if (this.context.isInBrowserMode()) {
            this.ngZone.runOutsideAngular(() => {
                this.window.addEventListener("scroll", this.onScroll);
            });
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        if (this.context.isInBrowserMode()) {
            this.window.removeEventListener("scroll", this.onScroll);
        }
    }

    public onChangeDisplayMode(value: DisplayMode): void {
        this.gridFacade.changeDisplayMode(value);
    }

    /**
     * when selected sort change
     */
    public onSortChanged(sort: GridSortByOption): void {
        this.gridFacade.updateGridSort(sort);
    }

    public onIsMobileFiltersDialogChange(
        isMobileFiltersDialogOpen: boolean,
    ): void {
        this.isMobileFiltersDialogOpen = isMobileFiltersDialogOpen;
        this.detector.markForCheck();
    }
}
